import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link } from 'react-router-dom';
import { Mail, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { Layout } from '../components/ui/Layout';
import { 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  Button, 
  ErrorMessage 
} from '../components/ui/Form';
import { theme } from '../styles/theme';
import { authService } from '../services/authService';

const InputContainer = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textTertiary};
  z-index: 1;
`;

const StyledInput = styled(Input)`
  padding-left: ${theme.spacing['3xl']};
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.primary};
  font-size: ${theme.fontSizes.sm};
  text-decoration: none;
  margin-bottom: ${theme.spacing.lg};
  
  &:hover {
    text-decoration: underline;
  }
`;

const InfoBox = styled.div`
  background-color: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.gray200};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
`;

const InfoText = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl} 0;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  background-color: ${theme.colors.success};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.lg};
  color: ${theme.colors.white};
`;

const SuccessTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const SuccessDescription = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing.xl} 0;
  line-height: 1.5;
`;

const validationSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
});

interface ForgotPasswordForm {
  email: string;
}

export const ForgotPassword: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ForgotPasswordForm>({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    setIsLoading(true);
    
    try {
      const response = await authService.resetPassword(data.email);
      
      if (response.success) {
        setSubmittedEmail(data.email);
        setIsSuccess(true);
      } else {
        if (response.error?.includes('email')) {
          setError('email', { message: response.error });
        } else {
          toast.error(response.error || 'Failed to send reset email');
        }
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (submittedEmail) {
      setIsLoading(true);
      try {
        const response = await authService.resetPassword(submittedEmail);
        if (response.success) {
          toast.success('Reset email sent again!');
        } else {
          toast.error('Failed to resend email');
        }
      } catch (error) {
        toast.error('An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (isSuccess) {
    return (
      <Layout
        title="Check Your Email"
        subtitle="We've sent you a password reset link"
      >
        <SuccessContainer>
          <SuccessIcon>
            <CheckCircle size={32} />
          </SuccessIcon>
          <SuccessTitle>Reset Link Sent</SuccessTitle>
          <SuccessDescription>
            We've sent a password reset link to <strong>{submittedEmail}</strong>.
            Please check your email and follow the instructions to reset your password.
          </SuccessDescription>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
            <Button 
              type="button" 
              $variant="outline"
              $fullWidth
              onClick={handleResendEmail}
              disabled={isLoading}
              $loading={isLoading}
            >
              {isLoading ? 'Sending...' : 'Resend Email'}
            </Button>
            
            <Button 
              as={Link} 
              to="/login"
              $variant="secondary"
              $fullWidth
            >
              Back to Sign In
            </Button>
          </div>
        </SuccessContainer>
      </Layout>
    );
  }

  return (
    <Layout
      title="Forgot Password"
      subtitle="Enter your email to reset your password"
    >
      <BackLink to="/login">
        <ArrowLeft size={16} />
        Back to Sign In
      </BackLink>

      <InfoBox>
        <InfoText>
          Enter the email address associated with your account and we'll send you 
          a link to reset your password.
        </InfoText>
      </InfoBox>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label htmlFor="email">Email Address</Label>
          <InputContainer>
            <InputIcon>
              <Mail size={20} />
            </InputIcon>
            <StyledInput
              id="email"
              type="email"
              placeholder="Enter your email address"
              $hasError={!!errors.email}
              {...register('email')}
            />
          </InputContainer>
          {errors.email && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.email.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <Button 
          type="submit" 
          $fullWidth 
          $loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? 'Sending...' : 'Send Reset Link'}
        </Button>

        <div style={{ textAlign: 'center', marginTop: theme.spacing.lg }}>
          <Link 
            to="/login"
            style={{ 
              color: theme.colors.textSecondary, 
              fontSize: theme.fontSizes.sm,
              textDecoration: 'none' 
            }}
          >
            Remember your password? Sign in
          </Link>
        </div>
      </Form>
    </Layout>
  );
};